/**
 * As relações de um registro, montadas do catálogo da API.
 *
 * **Oito telas que não existiam, e uma peça só.** Co-docência, locais
 * permitidos de uma oferta, turmas de origem de uma trilha, ofertas que
 * coincidem, limites de área por turma, limites de um professor por série,
 * intrajornadas e — a que bloqueava tudo — qual esquema a turma segue em cada
 * turno. Todas existiam na API desde o início; nenhuma tinha interface, e por
 * isso montar uma escola do zero pela tela era impossível.
 *
 * `/relacoes` já devolvia o que era preciso, e o comentário dela no servidor
 * dizia, literalmente, *"para a interface montar os campos sozinha"*. Isto é
 * a interface montando.
 *
 * **A mesma decisão da tela de cadastro, pelo mesmo motivo.** Oito blocos
 * escritos à mão divergem em três meses — um ganha confirmação de exclusão
 * que o outro não tem, outro chama "remover" de "tirar". Um bloco dirigido
 * por metadado não diverge porque não tem onde.
 */

import { useCallback, useEffect, useState } from 'react'

import { ErroApi, api, type Linha, type Recurso, type Relacao } from '../api'
import { Icon } from '../components/Icon'
import { NOTA, rotuloDoBloco, rotuloDoCampo } from '../relacoes'
import { rotuloDe, rotulosDistintos } from '../rotulos'

/** Como chamar uma linha quando só se tem o id dela. */


export function Relacoes({
  projeto,
  recurso,
  registroId,
  recursos,
}: {
  projeto: string
  recurso: Recurso
  /** O registro aberto. Relação só existe depois de o pai existir. */
  registroId: string
  /** O catálogo, para traduzir tabela → caminho e buscar as opções. */
  recursos: Recurso[]
}) {
  const [relacoes, setRelacoes] = useState<Relacao[]>([])

  useEffect(() => {
    api.relacoes().then(setRelacoes).catch(() => setRelacoes([]))
  }, [])

  // `/relacoes` fala em tabela (`aponta_para: 'professor'`) e o menu fala em
  // caminho (`professores`). O catálogo traz os dois, então a tradução não
  // precisa ser mantida aqui.
  const caminhoDaTabela = new Map(recursos.map((r) => [r.tabela, r.caminho]))
  const minhas = relacoes.filter((r) => r.pai === recurso.tabela)

  if (minhas.length === 0) return null

  return (
    // `data-tour="relacoes"` é o alvo do manual: o passo fala do bloco, e
    // não de um campo dentro dele.
    <div data-tour="relacoes" className="relacoes">
      {minhas.map((relacao) => (
        <Bloco
          key={relacao.caminho}
          projeto={projeto}
          relacao={relacao}
          registroId={registroId}
          caminhoDaTabela={caminhoDaTabela}
        />
      ))}
    </div>
  )
}

function Bloco({
  projeto,
  relacao,
  registroId,
  caminhoDaTabela,
}: {
  projeto: string
  relacao: Relacao
  registroId: string
  caminhoDaTabela: Map<string, string>
}) {
  const caminho = relacao.caminho.replace(':id', registroId)
  const [itens, setItens] = useState<Linha[]>([])
  const [opcoes, setOpcoes] = useState<Record<string, Linha[]>>({})
  const rotulosDe = (coluna: string) => rotulosDistintos(opcoes[coluna] ?? [])
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [salvo, setSalvo] = useState(false)

  const carregar = useCallback(async () => {
    try {
      setItens(await api.listarRelacao(projeto, caminho))
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível carregar')
    }
  }, [projeto, caminho])

  useEffect(() => {
    void carregar()
  }, [carregar])

  // As listas de onde saem as opções de cada coluna de referência.
  const tabelas = relacao.referencias.map((r) => r.aponta_para).join(',')
  useEffect(() => {
    let vivo = true
    void Promise.all(
      relacao.referencias.map(async (r) => {
        const recurso = caminhoDaTabela.get(r.aponta_para)
        const linhas = recurso ? await api.listar(projeto, recurso).catch(() => []) : []
        return [r.coluna, linhas] as const
      }),
    ).then((pares) => {
      if (vivo) setOpcoes(Object.fromEntries(pares))
    })
    return () => {
      vivo = false
    }
    // `tabelas` é a forma estável do array de referências.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projeto, tabelas])

  async function gravar(proximos: Linha[]) {
    setSalvando(true)
    setErro(null)
    try {
      // A API recusa linha com referência vazia, e com razão. Mandar só as
      // completas deixa a pessoa montar a linha em paz antes de gravar.
      const completos = proximos.filter((l) =>
        relacao.referencias.every((r) => !!l[r.coluna]),
      )
      setItens(await api.substituirRelacao(projeto, caminho, completos))
      setSalvo(true)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível gravar')
      await carregar()
    } finally {
      setSalvando(false)
    }
  }

  const mudar = (i: number, coluna: string, valor: unknown) => {
    setSalvo(false)
    setItens((atual) => atual.map((l, n) => (n === i ? { ...l, [coluna]: valor } : l)))
  }

  const incompletas = itens.filter((l) =>
    relacao.referencias.some((r) => !l[r.coluna]),
  ).length

  return (
    <section className="section">
      <div className="section__head">
        <h2>{rotuloDoBloco(relacao.caminho)}</h2>
        <span className="field__hint">
          {itens.length} {itens.length === 1 ? 'linha' : 'linhas'}
        </span>
      </div>

      {NOTA[relacao.caminho] && <p className="callout">{NOTA[relacao.caminho]}</p>}

      {erro && (
        <p className="state state--pendencia" role="alert">
          <Icon name="aviso" size={14} />
          {erro}
        </p>
      )}

      {itens.length === 0 && <p className="field__hint">Nada ligado aqui ainda.</p>}

      <ul className="stack">
        {itens.map((linha, i) => (
          <li className="row relacao__linha" key={String(linha.id ?? i)}>
            {relacao.referencias.map((ref) => (
              <label className="field relacao__campo" key={ref.coluna}>
                <span className="field__label">{ref.rotulo}</span>
                <select
                  className="select"
                  value={String(linha[ref.coluna] ?? '')}
                  onChange={(e) => mudar(i, ref.coluna, e.target.value || null)}
                >
                  <option value="">—</option>
                  {(opcoes[ref.coluna] ?? []).map((o) => (
                    <option key={String(o.id)} value={String(o.id)}>
                      {rotulosDe(ref.coluna).get(String(o.id)) ?? rotuloDe(o)}
                    </option>
                  ))}
                </select>
              </label>
            ))}

            {relacao.campos.map((campo) => (
              <label className="field relacao__campo relacao__campo--curto" key={campo}>
                <span className="field__label">{rotuloDoCampo(campo)}</span>
                <input
                  className="input input--mono"
                  type="number"
                  value={String(linha[campo] ?? '')}
                  onChange={(e) =>
                    mudar(i, campo, e.target.value === '' ? null : Number(e.target.value))
                  }
                />
              </label>
            ))}

            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                const proximos = itens.filter((_, n) => n !== i)
                setItens(proximos)
                void gravar(proximos)
              }}
            >
              Tirar
            </button>
          </li>
        ))}
      </ul>

      <div className="row">
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => {
            setSalvo(false)
            setItens((atual) => [...atual, {}])
          }}
        >
          Acrescentar
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={salvando}
          onClick={() => void gravar(itens)}
        >
          {salvando ? 'Gravando…' : 'Gravar'}
        </button>
        {incompletas > 0 && (
          <span className="field__hint">
            {incompletas} linha(s) por completar — elas não são gravadas.
          </span>
        )}
        {salvo && incompletas === 0 && <span className="field__hint">Gravado.</span>}
      </div>
    </section>
  )
}
